import { useQuery } from '@apollo/client'
import type { PromptResolve } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { RegularModal } from '@/components_deprecated'
import { format } from '@/utils'
import { ActionLogsList } from './action-logs-list'
import type { ActionFieldRecord } from '../absence-details/absence-logs/util'
import {
  quillDeltaToPlainText,
  tryBuildQuillDelta,
} from '../absence-details/absence-logs/util'
import { TIME_ENTRY_DETAILS_QUERY } from '../queries'
import type { TimeEntryDetailsNode, TimeEntryLogNode } from '../types.graphql'

interface ViewTimeEntryLogsModalProps {
  onClose: PromptResolve<boolean | null>
  timeEntryId: string
}

interface TimeEntryLogTranslations {
  actionLog: string
  noActivity: string
  close: string
  loading: string
  unknown: string
  start: string
  end: string
  pause: string
  project: string
  department: string
  salaryCodeOrdinary: string
  salaryCodeOvertime: string
  overtimeMode: string
  diet: string
  approved: string
  comment: string
  fromTerminal: string
  mergeOverride: string
  mergedWith: string
  yes: string
  no: string
  timeEntry: string
  logSummary: (values: {
    user: string
    action: string
    target: string
  }) => string
  logActionCreated: string
  logActionUpdated: string
  logActionDeleted: string
  logShowDetails: string
  logHideDetails: string
  logTabNew: string
  logTabOld: string
}

function normalizeAction(value: string): string {
  return value.toUpperCase()
}

function getOrderedFieldEntries(fields: ActionFieldRecord) {
  const order: Record<string, number> = {
    datetime_start: 10,
    datetime_end: 11,
    pause: 20,
    project_id: 30,
    department_id: 40,
    salary_code_ordinary_id: 50,
    salary_code_overtime_id: 51,
    overtime_mode: 60,
    diet: 70,
    approved: 80,
    comment: 90,
    from_terminal: 100,
    merge_override: 110,
    merged_with_time_entry_id: 111,
  }

  return Object.entries(fields).sort(([fieldA], [fieldB]) => {
    const rankA = order[fieldA] ?? 999
    const rankB = order[fieldB] ?? 999
    if (rankA !== rankB) return rankA - rankB
    return fieldA.localeCompare(fieldB)
  })
}

function formatSecondsAsHoursMinutes(raw: unknown): string {
  const seconds =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string'
      ? Number.parseFloat(raw)
      : Number.NaN
  if (!Number.isFinite(seconds)) return String(raw)

  const totalMinutes = Math.round(seconds / 60)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = Math.abs(totalMinutes % 60)
  return `${hours}:${String(minutes).padStart(2, '0')}`
}

function formatBooleanValue(
  raw: unknown,
  yesLabel: string,
  noLabel: string
): string {
  if (raw === true || raw === 'true') return yesLabel
  if (raw === false || raw === 'false') return noLabel
  return String(raw)
}

function formatIdLabel(
  raw: unknown,
  currentId: string | undefined,
  currentName: string | undefined
): string {
  if (raw == null || raw === '') return '-'

  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const relation = raw as { id?: unknown; name?: unknown }
    if (typeof relation.name === 'string' && relation.name) return relation.name
    if (relation.id != null) return `#${relation.id}`
    return '-'
  }

  if (String(raw) === currentId && currentName) return currentName
  return `#${raw}`
}

function formatActionFieldValue(
  field: string,
  raw: unknown,
  timeEntry: TimeEntryDetailsNode | null,
  yesLabel: string,
  noLabel: string
): string {
  if (raw == null || raw === '') return '-'

  const quillDelta = tryBuildQuillDelta(raw)
  if (quillDelta) return quillDeltaToPlainText(quillDelta)

  if (
    (field === 'datetime_start' || field === 'datetime_end') &&
    typeof raw === 'string'
  ) {
    const date = new Date(raw)
    if (!Number.isNaN(date.getTime())) return format(date, 'dd.MM.yyyy HH:mm')
  }

  if (field === 'pause') return formatSecondsAsHoursMinutes(raw)

  if (field === 'facility_id') {
    return formatIdLabel(
      raw,
      timeEntry?.facility?.id,
      timeEntry?.facility?.name
    )
  }

  if (field === 'department_id') {
    return formatIdLabel(
      raw,
      timeEntry?.department?.id,
      timeEntry?.department?.name
    )
  }

  if (field === 'salary_code_ordinary_id') {
    return formatIdLabel(
      raw,
      timeEntry?.salaryCodeOrdinary?.id,
      timeEntry?.salaryCodeOrdinary?.name
    )
  }

  if (field === 'salary_code_overtime_id') {
    return formatIdLabel(
      raw,
      timeEntry?.salaryCodeOvertime?.id,
      timeEntry?.salaryCodeOvertime?.name
    )
  }

  if (field === 'diet' || field === 'approved' || field === 'from_terminal') {
    return formatBooleanValue(raw, yesLabel, noLabel)
  }

  if (field === 'overtime_mode' && typeof raw === 'string') {
    return raw.replaceAll('_', ' ').toLowerCase()
  }

  if (Array.isArray(raw)) {
    if (!raw.length) return '-'
    return raw
      .map(entry =>
        formatActionFieldValue(field, entry, timeEntry, yesLabel, noLabel)
      )
      .join(', ')
  }

  if (raw && typeof raw === 'object') {
    const objectValue = raw as Record<string, unknown>
    const formattedObject = Object.entries(objectValue)
      .filter(([key]) => key !== 'id')
      .map(([key, value]) => {
        const formattedValue = formatActionFieldValue(
          field,
          value,
          timeEntry,
          yesLabel,
          noLabel
        )
        return `${key.replaceAll('_', ' ')}: ${formattedValue}`
      })
      .join(', ')

    return formattedObject || '-'
  }

  return String(raw)
}

function getActionFieldLabel(
  field: string,
  translations: TimeEntryLogTranslations
) {
  if (field === 'datetime_start') return translations.start
  if (field === 'datetime_end') return translations.end
  if (field === 'pause') return translations.pause
  if (field === 'project_id') return translations.project
  if (field === 'department_id') return translations.department
  if (field === 'salary_code_ordinary_id')
    return translations.salaryCodeOrdinary
  if (field === 'salary_code_overtime_id')
    return translations.salaryCodeOvertime
  if (field === 'overtime_mode') return translations.overtimeMode
  if (field === 'diet') return translations.diet
  if (field === 'approved') return translations.approved
  if (field === 'comment') return translations.comment
  if (field === 'from_terminal') return translations.fromTerminal
  if (field === 'merge_override') return translations.mergeOverride
  if (field === 'merged_with_time_entry_id') return translations.mergedWith
  return field.replaceAll('_', ' ')
}

function getLogActionWord(
  action: string,
  translations: TimeEntryLogTranslations
) {
  const normalizedAction = normalizeAction(action)
  if (normalizedAction === 'CREATED') return translations.logActionCreated
  if (normalizedAction === 'UPDATED') return translations.logActionUpdated
  if (normalizedAction === 'DELETED') return translations.logActionDeleted
  return action.toLowerCase()
}

function getLogSummary(
  log: TimeEntryLogNode,
  fields: ActionFieldRecord,
  translations: TimeEntryLogTranslations
) {
  const orderedFieldKeys = getOrderedFieldEntries(fields).map(
    ([field]) => field
  )
  const actorName = log.user?.fullName || translations.unknown
  const actionWord = getLogActionWord(log.action, translations)

  if (!orderedFieldKeys.length) {
    return translations.logSummary({
      user: actorName,
      action: actionWord,
      target: translations.timeEntry.toLowerCase(),
    })
  }

  const targets = orderedFieldKeys
    .map(field => getActionFieldLabel(field, translations).toLowerCase())
    .join(', ')

  return translations.logSummary({
    user: actorName,
    action: actionWord,
    target: targets,
  })
}

export function ViewTimeEntryLogsModal({
  onClose,
  timeEntryId,
}: ViewTimeEntryLogsModalProps) {
  const translations = useTranslate({
    actionLog: 'issues.action-log',
    noActivity: 'absence.details.log.no-activity',
    close: 'common.close',
    loading: 'common.loading',
    unknown: 'common.unknown',
    start: 'common.start',
    end: 'common.end',
    pause: 'common.pause',
    project: 'common.project',
    department: 'common.department',
    salaryCodeOrdinary: 'common.salary-code',
    salaryCodeOvertime: 'common.salary-code-if-overtime',
    overtimeMode: 'common.overtime-mode',
    diet: 'common.diet',
    approved: 'timesheets.absence-approved',
    comment: 'common.comment',
    fromTerminal: 'common.terminal',
    mergeOverride: 'timesheets.time-entry-merge-override',
    mergedWith: 'timesheets.time-entry-merged-with',
    yes: 'common.yes',
    no: 'common.no',
    timeEntry: 'timesheets.timeentry',
    logSummary: [
      'absence.details.log.summary',
      { user: '', action: '', target: '' },
    ],
    logActionCreated: 'absence.details.log.action-created',
    logActionUpdated: 'absence.details.log.action-updated',
    logActionDeleted: 'absence.details.log.action-deleted',
    logShowDetails: 'absence.details.log.show-details',
    logHideDetails: 'absence.details.log.hide-details',
    logTabNew: 'absence.details.log.tab-new',
    logTabOld: 'absence.details.log.tab-old',
  }) as TimeEntryLogTranslations

  const { data, loading, error } = useQuery(TIME_ENTRY_DETAILS_QUERY, {
    variables: { id: timeEntryId },
    fetchPolicy: 'cache-and-network',
  })

  const timeEntry = data?.timeEntry ?? null
  const logs = [...(timeEntry?.actionLogs ?? [])].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )

  return (
    <RegularModal
      title={translations.actionLog}
      width="760px"
      cancelText={translations.close}
      onCancel={() => onClose(null)}
      cancelOnClickOutside
      overflowY="auto"
    >
      <div className="max-h-[70vh] overflow-y-auto">
        {loading && !timeEntry ? (
          <p className="px-5 py-6 text-sm text-stone-600">
            {translations.loading}
          </p>
        ) : error ? (
          <p className="px-5 py-6 text-sm text-red-600">{error.message}</p>
        ) : (
          <ActionLogsList
            logs={logs}
            noActivityText={translations.noActivity}
            showDetailsText={translations.logShowDetails}
            hideDetailsText={translations.logHideDetails}
            oldTabLabel={translations.logTabOld}
            newTabLabel={translations.logTabNew}
            getLogSummary={(log, fields) =>
              getLogSummary(log, fields, translations)
            }
            getOrderedFieldEntries={getOrderedFieldEntries}
            getFieldLabel={field => getActionFieldLabel(field, translations)}
            formatFieldValue={(field, raw) =>
              formatActionFieldValue(
                field,
                raw,
                timeEntry,
                translations.yes,
                translations.no
              )
            }
            shouldUseOldNewTabs={({ fieldName, value, oldValue, newValue }) =>
              oldValue !== newValue &&
              (fieldName === 'comment' ||
                !!tryBuildQuillDelta(value.old_value) ||
                !!tryBuildQuillDelta(value.new_value))
            }
            rowClassName="py-3"
            emptyClassName="px-5 py-6 text-sm text-stone-500"
          />
        )}
      </div>
    </RegularModal>
  )
}
